"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import { formatPrice } from "@/lib/price";

type PublicCategory = {
  id: string;
  name_tr: string;
  name_en: string;
  sortOrder: number;
  isActive: boolean;
};

type PublicProduct = {
  id: string;
  categoryId: string;
  name_tr: string;
  name_en: string;
  desc_tr: string | null;
  desc_en: string | null;
  description?: string | null;
  price: string;
  oldPrice: string | null;
  bottlePrice: string | null;
  currency: string;
  imageUrl: string | null;
  sortOrder: number;
  isPopular: boolean;
  isPromo: boolean;
  isNew: boolean;
  isNonAlcoholic: boolean;
  tags: string[];
  status: "available" | "sold_out";
};

type VenueInfo = {
  name: string;
  phone: string | null;
  address: string | null;
  instagram: string | null;
  wifiName: string | null;
  wifiPassword: string | null;
  showWifi: boolean;
  announcementText: string | null;
};

type Props = {
  slug: string;
  lang: "tr" | "en";
  mobileWallpaperUrl?: string | null;
  desktopWallpaperUrl?: string | null;
  mobileOverlayUrl?: string | null;
  desktopOverlayUrl?: string | null;
  mobilePatternUrl?: string | null;
  desktopPatternUrl?: string | null;
  mobileHeaderBannerUrl?: string | null;
  desktopHeaderBannerUrl?: string | null;
  headerBgMobileUrl?: string | null;
  headerBgDesktopUrl?: string | null;
  gridBgMobileUrl?: string | null;
  gridBgDesktopUrl?: string | null;
  venue: VenueInfo;
  categories: PublicCategory[];
  products: PublicProduct[];
};

export function MenuPageClient({
  slug,
  lang,
  mobileWallpaperUrl,
  desktopWallpaperUrl,
  mobileOverlayUrl,
  desktopOverlayUrl,
  mobilePatternUrl,
  desktopPatternUrl,
  mobileHeaderBannerUrl,
  desktopHeaderBannerUrl,
  headerBgMobileUrl,
  headerBgDesktopUrl,
  gridBgMobileUrl,
  gridBgDesktopUrl,
  venue,
  categories,
  products
}: Props) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortMode, setSortMode] = useState<
    "default" | "priceAsc" | "priceDesc" | "nameAsc"
  >("default");
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [headerParallax, setHeaderParallax] = useState(0);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  
  // FAZ 8: Smart perception state
  const [scrollVelocity, setScrollVelocity] = useState(0);
  const [timeAtTop, setTimeAtTop] = useState(0);
  const [adaptiveBlur, setAdaptiveBlur] = useState(0);
  const [adaptivePatternOpacity, setAdaptivePatternOpacity] = useState(1);
  
  // FAZ 9: Event tracking
  const trackEvent = useCallback(async (
    type: "PAGE_VIEW" | "CATEGORY_VIEW" | "PRODUCT_VIEW" | "PRODUCT_ADD_INTENT" | "MEDIA_VIEW",
    data?: { categoryId?: string; productId?: string; mediaAssetId?: string }
  ) => {
    try {
      await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venueSlug: slug,
          type,
          categoryId: data?.categoryId || null,
          productId: data?.productId || null,
          mediaAssetId: data?.mediaAssetId || null,
          path: window.location.pathname
        })
      });
    } catch (err) {
      // Silent fail - tracking should not break the app
    }
  }, [slug]);

  // Helper: Check if event should be sent (dedup with sessionStorage + TTL)
  const shouldSendEventOnce = useCallback((key: string, ttlMs: number = 30000): boolean => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const timestamp = parseInt(stored, 10);
        if (Date.now() - timestamp < ttlMs) {
          return false; // Too soon, skip
        }
      }
      sessionStorage.setItem(key, Date.now().toString());
      return true;
    } catch (err) {
      // sessionStorage unavailable (private mode, etc.) - allow send
      return true;
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 200);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!selectedCategoryId && categories[0]) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  // FAZ 8: Smart perception - scroll velocity tracking
  useEffect(() => {
    const motionOff = document.documentElement.getAttribute("data-motion") === "off";
    if (motionOff || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let lastScrollY = window.scrollY;
    let lastTime = Date.now();

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const now = Date.now();
      const deltaY = Math.abs(scrollY - lastScrollY);
      const deltaTime = now - lastTime;
      
      // Calculate velocity (px/ms)
      const velocity = deltaTime > 0 ? deltaY / deltaTime : 0;
      setScrollVelocity(velocity);
      
      // Adaptive parallax: slower scroll = more parallax
      const parallaxFactor = velocity > 0.5 ? 0.05 : 0.1; // Fast scroll = less parallax
      const maxParallax = velocity > 0.5 ? 4 : 8; // Fast scroll = max 4px
      const parallaxValue = Math.min(scrollY * parallaxFactor, maxParallax);
      setHeaderParallax(parallaxValue);

      // Track time at top
      if (scrollY < 50) {
        setTimeAtTop(prev => prev + (deltaTime / 1000)); // seconds
      } else {
        setTimeAtTop(0);
      }

      lastScrollY = scrollY;
      lastTime = now;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // FAZ 8: Adaptive blur based on time at top
  useEffect(() => {
    const motionOff = document.documentElement.getAttribute("data-motion") === "off";
    if (motionOff || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    if (timeAtTop > 3) {
      // After 3 seconds at top, slightly increase blur (1-2px)
      setAdaptiveBlur(Math.min(2, (timeAtTop - 3) * 0.5));
    } else {
      setAdaptiveBlur(0);
    }
  }, [timeAtTop]);

  // Section fade + lift (IntersectionObserver)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const sections = document.querySelectorAll("section[id^='cat-']");
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [categories.length]); // Re-run when categories change

  // FAZ 9.5: Viewport impression tracking (PRODUCT_VIEW & CATEGORY_VIEW)
  useEffect(() => {
    // Product cards observer: %35 threshold
    const productObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            const productId = entry.target.getAttribute("data-product-id");
            const categoryId = entry.target.getAttribute("data-category-id");
            if (productId && shouldSendEventOnce(`pv_${productId}`, 30000)) {
              trackEvent("PRODUCT_VIEW", { productId, categoryId: categoryId || undefined });
            }
          }
        });
      },
      { threshold: 0.35, rootMargin: "0px" }
    );

    // Category sections observer: %25 threshold
    const categoryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            const categoryId = entry.target.getAttribute("data-category-id");
            if (categoryId && categoryId !== "all" && shouldSendEventOnce(`cv_${categoryId}`, 30000)) {
              trackEvent("CATEGORY_VIEW", { categoryId });
            }
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px" }
    );

    // Observe product cards
    const productCards = document.querySelectorAll("[data-product-id]");
    productCards.forEach((card) => productObserver.observe(card));

    // Observe category sections
    const categorySections = document.querySelectorAll("[data-category-id]");
    categorySections.forEach((section) => categoryObserver.observe(section));

    return () => {
      productCards.forEach((card) => productObserver.unobserve(card));
      categorySections.forEach((section) => categoryObserver.unobserve(section));
    };
  }, [products, categories, trackEvent, shouldSendEventOnce]);

  // FAZ 9: Track PAGE_VIEW on mount
  useEffect(() => {
    trackEvent("PAGE_VIEW");
  }, [trackEvent]);

  const productsByCategory = useMemo(() => {
    const map: Record<string, PublicProduct[]> = {};
    for (const p of products) {
      if (!map[p.categoryId]) map[p.categoryId] = [];
      map[p.categoryId].push(p);
    }
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => a.sortOrder - b.sortOrder)
    );
    return map;
  }, [products]);

  const filteredByCategory = (categoryId: string) => {
    let list =
      categoryId === "all"
        ? Object.values(productsByCategory).flat()
        : productsByCategory[categoryId] ?? [];

    // sold_out ürünler zaten backend'de filtreleniyor, burada ekstra filtreleme yok

    if (debouncedSearch) {
      list = list.filter((p) => {
        const name =
          lang === "tr" ? p.name_tr.toLowerCase() : p.name_en.toLowerCase();
        const desc =
          (lang === "tr" ? p.desc_tr ?? "" : p.desc_en ?? "").toLowerCase();
        return (
          name.includes(debouncedSearch) || desc.includes(debouncedSearch)
        );
      });
    }

    if (sortMode === "priceAsc") {
      list = [...list].sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortMode === "priceDesc") {
      list = [...list].sort((a, b) => Number(b.price) - Number(a.price));
    } else if (sortMode === "nameAsc") {
      list = [...list].sort((a, b) => {
        const an = (lang === "tr" ? a.name_tr : a.name_en).toLowerCase();
        const bn = (lang === "tr" ? b.name_tr : b.name_en).toLowerCase();
        return an.localeCompare(bn);
      });
    }

    return list;
  };

  function scrollToCategory(id: string) {
    const el = document.getElementById(`cat-${id}`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: "smooth" });
    setSelectedCategoryId(id);
    // FAZ 9: Track category view
    trackEvent("CATEGORY_VIEW", { categoryId: id });
  }

  const visibleCategories =
    selectedCategoryId && selectedCategoryId !== "all"
      ? categories.filter((c) => c.id === selectedCategoryId)
      : categories;

  // Use mobile as default, fallback to desktop
  const wallpaperUrl = mobileWallpaperUrl || desktopWallpaperUrl || null;
  const overlayUrl = mobileOverlayUrl || desktopOverlayUrl || null;
  const patternUrl = mobilePatternUrl || desktopPatternUrl || null;

  return (
    <div className="menu-bg">
      {/* 1) Wallpaper layer */}
      {wallpaperUrl && (
        <div
          className="menu-bg__wallpaper"
          style={{ backgroundImage: `url(${wallpaperUrl})` }}
          aria-hidden="true"
        />
      )}

      {/* 2) Pattern layer - FAZ 8: Visual fatigue prevention */}
      {patternUrl && (
        <div
          className="menu-bg__pattern"
          style={{ 
            backgroundImage: `url(${patternUrl})`,
            opacity: adaptivePatternOpacity * 0.25 // Base 0.25, reduced if too many layers
          }}
          aria-hidden="true"
        />
      )}

      {/* 3) Overlay layer (gradient/smoke/grain + optional overlay image) */}
      <div className="menu-bg__overlay" aria-hidden="true">
        {overlayUrl && (
          <div
            className="menu-bg__overlayImage"
            style={{ backgroundImage: `url(${overlayUrl})` }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* 4) Content */}
      <div className="menu-bg__content text-text overflow-x-hidden">
        <header 
          className="menu-header sticky top-0 z-20 border-b border-border/80 bg-background/85 backdrop-blur-md parallax-soft"
          style={{ transform: `translateY(${headerParallax}px)` }}
        >
          {/* Header Banner layer */}
          {mobileHeaderBannerUrl && (
            <div
              className="menu-header__banner menu-header__banner--mobile"
              style={{ backgroundImage: `url(${mobileHeaderBannerUrl})` }}
              aria-hidden="true"
            />
          )}
          {desktopHeaderBannerUrl && (
            <div
              className="menu-header__banner menu-header__banner--desktop"
              style={{ backgroundImage: `url(${desktopHeaderBannerUrl})` }}
              aria-hidden="true"
            />
          )}
          <div className="menu-header__bannerOverlay" aria-hidden="true" />
          {/* Header Background layer */}
          {headerBgMobileUrl && (
            <div
              className="menu-header__bg menu-header__bg--mobile"
              style={{ backgroundImage: `url(${headerBgMobileUrl})` }}
              aria-hidden="true"
            />
          )}
          {headerBgDesktopUrl && (
            <div
              className="menu-header__bg menu-header__bg--desktop"
              style={{ backgroundImage: `url(${headerBgDesktopUrl})` }}
              aria-hidden="true"
            />
          )}
          <div 
            className="menu-header__bgOverlay" 
            aria-hidden="true"
            style={{
              backdropFilter: `blur(calc(var(--brand-header-overlay-blur, 10px) + ${adaptiveBlur}px))`,
              WebkitBackdropFilter: `blur(calc(var(--brand-header-overlay-blur, 10px) + ${adaptiveBlur}px))`
            }}
          />
          <div className="menu-top-panel mx-auto max-w-lg px-4 py-4 md:py-6 space-y-3">
          <div className="flex items-center justify-between gap-3 text-[11px] text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            <div className="flex-1 min-w-0">
              {venue.address && <span>{venue.address}</span>}
            </div>
            <div className="flex flex-col items-end gap-1">
              {venue.instagram && (
                <a
                  href={`https://instagram.com/${venue.instagram.replace(
                    /^@/,
                    ""
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-dotted text-primary"
                >
                  Instagram
                </a>
              )}
              {venue.phone && (
                <a
                  href={`tel:${venue.phone}`}
                  className="px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/50"
                >
                  Ara: {venue.phone}
                </a>
              )}
            </div>
          </div>
          {venue.showWifi && venue.wifiName && (
            <div className="rounded-xl border border-secondary/60 bg-secondary/15 px-3 py-2 text-[11px] text-secondary flex items-center justify-between gap-2">
              <div>
                <span className="font-semibold mr-1">Wi‑Fi</span>
                <span className="text-text/90">
                  {venue.wifiName}
                  {venue.wifiPassword ? ` / ${venue.wifiPassword}` : null}
                </span>
              </div>
            </div>
          )}
          {venue.announcementText && (
            <div className="rounded-xl border border-primary/60 bg-primary/10 px-3 py-2 text-[11px] text-primary">
              {venue.announcementText}
            </div>
          )}
          <div className="pt-1 pb-1 space-y-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                lang === "tr"
                  ? "İçecek veya açıklama ara..."
                  : "Search drinks..."
              }
              className="w-full rounded-full bg-background/90 border border-border/80 px-4 py-2 text-xs text-white placeholder:text-white/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
            />
            {/* Mobil: kategori & filtre */}
            <div className="flex flex-col gap-2 md:hidden">
              <button
                type="button"
                onClick={() => setCategorySheetOpen(true)}
                className="w-full rounded-xl border border-border bg-card/80 px-3 py-3 text-xs text-text flex items-center justify-between min-h-[52px]"
              >
                <span className="uppercase tracking-[0.18em] text-[10px] text-muted">
                  {lang === "tr" ? "Hızlı Kategori" : "Quick Category"}
                </span>
                <span className="text-[11px] text-muted">▾</span>
              </button>

              <div className="flex flex-col gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-muted">
                    {lang === "tr" ? "Sırala" : "Sort"}
                  </label>
                  <select
                    value={sortMode}
                    onChange={(e) =>
                      setSortMode(e.target.value as typeof sortMode)
                    }
                    className="w-full rounded-xl border border-border bg-card/80 px-3 py-2 text-xs text-text focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary min-h-[44px]"
                  >
                    <option value="default">
                      {lang === "tr" ? "Varsayılan" : "Default"}
                    </option>
                    <option value="priceAsc">
                      {lang === "tr" ? "Fiyat (Artan)" : "Price (Low → High)"}
                    </option>
                    <option value="priceDesc">
                      {lang === "tr" ? "Fiyat (Azalan)" : "Price (High → Low)"}
                    </option>
                    <option value="nameAsc">
                      {lang === "tr" ? "A‑Z" : "A‑Z"}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop: filtre ve sıralama */}
            <div className="hidden md:flex items-center justify-end gap-4 text-[11px]">
              <div className="flex items-center gap-2">
                <span className="text-muted">
                  {lang === "tr" ? "Sırala" : "Sort"}
                </span>
                <select
                  value={sortMode}
                  onChange={(e) =>
                    setSortMode(e.target.value as typeof sortMode)
                  }
                  className="rounded-full border border-border bg-card/80 px-3 py-1.5 text-[11px] text-text focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary min-h-[40px]"
                >
                  <option value="default">
                    {lang === "tr" ? "Varsayılan" : "Default"}
                  </option>
                  <option value="priceAsc">
                    {lang === "tr" ? "Fiyat (Artan)" : "Price (Low → High)"}
                  </option>
                  <option value="priceDesc">
                    {lang === "tr" ? "Fiyat (Azalan)" : "Price (High → Low)"}
                  </option>
                  <option value="nameAsc">
                    {lang === "tr" ? "A‑Z" : "A‑Z"}
                  </option>
                </select>
              </div>
            </div>
          </div>
          </div>
        {/* Desktop kategori chip bar */}
        <div className="border-t border-border/70 bg-background/90 backdrop-blur-md hidden md:block">
          <div className="mx-auto max-w-3xl px-4 py-2">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => setSelectedCategoryId("all")}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] border min-h-[40px] ${
                  !selectedCategoryId || selectedCategoryId === "all"
                    ? "bg-primary text-black border-primary shadow-[0_0_10px_rgba(242,169,0,0.7)]"
                    : "bg-card/80 text-muted border-border/70 hover:border-primary/60 hover:text-text"
                }`}
              >
                {lang === "tr" ? "Tümü" : "All"}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setSelectedCategoryId(c.id);
                    scrollToCategory(c.id);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] border min-h-[40px] ${
                    selectedCategoryId === c.id
                      ? "bg-primary text-black border-primary shadow-[0_0_10px_rgba(242,169,0,0.7)]"
                      : "bg-card/80 text-muted border-border/70 hover:border-primary/60 hover:text-text"
                  }`}
                >
                  {lang === "tr" ? c.name_tr : c.name_en}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="menu-grid">
        {/* Grid Background layer */}
        {(gridBgMobileUrl || gridBgDesktopUrl) && (
          <>
            {gridBgMobileUrl && (
              <div
                className="menu-grid__bg menu-grid__bg--mobile"
                style={{ backgroundImage: `url(${gridBgMobileUrl})` }}
                aria-hidden="true"
              />
            )}
            {gridBgDesktopUrl && (
              <div
                className="menu-grid__bg menu-grid__bg--desktop"
                style={{ backgroundImage: `url(${gridBgDesktopUrl})` }}
                aria-hidden="true"
              />
            )}
          </>
        )}

        <div className="menu-grid__content mx-auto max-w-md px-4 pb-10 pt-3 space-y-6">
          {visibleCategories.map((category) => {
          const items = filteredByCategory(category.id);
          if (items.length === 0) return null;
          return (
            <section
              key={category.id}
              id={`cat-${category.id}`}
              data-category-id={category.id}
              className={`scroll-mt-24 space-y-3 ${
                visibleSections.has(`cat-${category.id}`) ? "micro-lift" : "opacity-0"
              }`}
            >
              <h2 className="text-base font-semibold tracking-tight">
                {lang === "tr" ? category.name_tr : category.name_en}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {items.map((p) => {
                  const hasImage = Boolean(p.imageUrl);
                  const name = lang === "tr" ? p.name_tr : p.name_en;
                  const desc =
                    lang === "tr"
                      ? p.desc_tr ?? p.desc_en
                      : p.desc_en ?? p.desc_tr;
                  return (
                    <article
                      key={p.id}
                      data-product-id={p.id}
                      data-category-id={category.id}
                      className="rounded-2xl border border-border/80 bg-card/80 backdrop-blur-sm shadow-[0_8px_24px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col product-card-hover cursor-pointer"
                      onClick={() => {
                        // Click tracking (always send, bypasses dedup)
                        trackEvent("PRODUCT_VIEW", { productId: p.id, categoryId: category.id });
                      }}
                    >
                      {hasImage && (
                        <div className="aspect-[4/3] w-full overflow-hidden">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={p.imageUrl as string}
                            alt={name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                        <div className="px-3 py-2 flex-1 flex flex-col justify-between gap-1">
                        <div>
                          <h3 className="text-sm font-semibold text-text line-clamp-2 min-h-[2.5rem]">
                            {name}
                          </h3>
                          {desc && (
                            <p className="text-[11px] text-muted line-clamp-1">
                              {desc}
                            </p>
                          )}
                        </div>
                        <div className="mt-1 flex items-center justify-end gap-2">
                          <div className="text-xs font-bold text-primary text-right">
                            {Number(p.price) === 0 ? (
                              "—"
                            ) : p.bottlePrice && Number(p.bottlePrice) > 0 ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span>
                                  {lang === "tr" ? "Bardak" : "Glass"}:{" "}
                                  {formatPrice(p.price, p.currency)}
                                </span>
                                <span>
                                  {lang === "tr" ? "Şişe" : "Bottle"}:{" "}
                                  {formatPrice(p.bottlePrice, p.currency)}
                                </span>
                              </div>
                            ) : (
                              formatPrice(p.price, p.currency)
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}

        {visibleCategories.every((c) => filteredByCategory(c.id).length === 0) && (
          <div className="pt-20 text-center text-sm text-muted">
            {lang === "tr"
              ? "Uygun ürün bulunamadı. Filtreleri veya arama terimini değiştirin."
              : "No matching items. Try adjusting filters or search."}
          </div>
        )}
        </div>
      </main>

      {/* Mobil hızlı kategori sheet */}
      {categorySheetOpen && (
        <div className="fixed inset-0 z-30 flex items-end justify-center bg-text/5 backdrop-blur-sm md:hidden">
          <div className="w-full max-w-md rounded-t-3xl border border-border bg-card px-4 pt-4 pb-6 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted">
                {lang === "tr" ? "Hızlı Kategori" : "Quick Category"}
              </p>
              <button
                type="button"
                onClick={() => setCategorySheetOpen(false)}
                className="text-xs text-muted hover:text-text px-2 py-1"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategoryId("all");
                  setCategorySheetOpen(false);
                }}
                className={`w-full rounded-xl border px-3 py-3 text-xs min-h-[52px] ${
                  !selectedCategoryId || selectedCategoryId === "all"
                    ? "bg-primary text-black border-primary"
                    : "bg-card/80 text-text border-border"
                }`}
              >
                {lang === "tr" ? "Tümü" : "All"}
              </button>
              {categories.map((c) => {
                const active = selectedCategoryId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelectedCategoryId(c.id);
                      setCategorySheetOpen(false);
                      scrollToCategory(c.id);
                    }}
                    className={`w-full rounded-xl border px-3 py-3 text-xs min-h-[52px] ${
                      active
                        ? "bg-primary text-black border-primary"
                        : "bg-card/80 text-text border-border"
                    }`}
                  >
                    {lang === "tr" ? c.name_tr : c.name_en}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setCategorySheetOpen(false)}
              className="w-full rounded-xl border border-border bg-background px-3 py-3 text-xs text-text min-h-[48px]"
            >
              {lang === "tr" ? "Kapat" : "Close"}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function Badge({
  children,
  tone
}: {
  children: React.ReactNode;
  tone: "primary" | "neutral" | "muted";
}) {
  const styles: Record<
    typeof tone,
    { bg: string; border: string; text: string }
  > = {
    primary: {
      bg: "bg-primary/15",
      border: "border-primary/40",
      text: "text-primary"
    },
    neutral: {
      bg: "bg-white/5",
      border: "border-white/10",
      text: "text-text"
    },
    muted: {
      bg: "bg-white/3",
      border: "border-white/8",
      text: "text-muted"
    }
  };

  const style = styles[tone];

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] border ${style.bg} ${style.border} ${style.text}`}
    >
      {children}
    </span>
  );
}


